import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClient } from '@/contexts/ClientContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users } from 'lucide-react';

export default function ClientSelector() {
  const { clientType, setClientType, availableRoles, staffRoleOptions } = useClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Prefer the account's REAL team roster (Name · Role). Fall back to the
  // generic industry role templates only when no staff have been set up.
  const hasStaff = Array.isArray(staffRoleOptions) && staffRoleOptions.length > 0;
  const options = hasStaff ? staffRoleOptions : availableRoles;

  const handleChange = (value) => {
    const selected = options.find((role) => role.id === value);
    if (selected) {
      setClientType(selected);
      // Navigate to dashboard when position changes (if not already there)
      if (!location.pathname.startsWith('/app') || location.pathname !== '/app') {
        navigate('/app');
      }
    }
  };

  const currentLabel =
    clientType?.label ||
    options.find((r) => r.id === clientType?.id)?.label ||
    (hasStaff ? 'Select Person' : 'Select Role');

  return (
    <div className="flex items-center gap-2">
      <Users className="w-4 h-4 text-muted-foreground hidden sm:block" />
      <Select value={clientType?.id || options[0]?.id} onValueChange={handleChange}>
        <SelectTrigger
          className="w-[150px] sm:w-[220px] max-w-[48vw] h-9 glass border-border/50 text-xs sm:text-sm"
          data-testid="client-role-selector"
        >
          <SelectValue>
            <span className="truncate block text-left">{currentLabel}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="glass-strong border-border/50 max-h-[320px]">
          {hasStaff && (
            <div
              className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              data-testid="client-role-team-heading"
            >
              Your team
            </div>
          )}
          {options.map((role) => (
            <SelectItem key={role.id} value={role.id} data-testid={`client-role-option-${role.id}`}>
              {hasStaff ? (
                <span className="flex flex-col leading-tight">
                  <span className="font-medium">{role.name || role.label}</span>
                  {role.roleLabel && (
                    <span className="text-[11px] text-muted-foreground">{role.roleLabel}</span>
                  )}
                </span>
              ) : (
                role.label
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
